import * as React from 'react'
import { useTheme } from '../providers/ThemeProvider'

// ═══════════════════════════════════════════════════════════════════════════
// Tabs - Tab Dividers / Bookmark Style for Diary Theme
// ═══════════════════════════════════════════════════════════════════════════

interface TabsContextType {
  value: string
  onValueChange: (value: string) => void
}

const TabsContext = React.createContext<TabsContextType | null>(null)

interface TabsProps {
  defaultValue: string
  value?: string
  onValueChange?: (value: string) => void
  className?: string
  children: React.ReactNode
}

export function Tabs({ defaultValue, value, onValueChange, className = '', children }: TabsProps) {
  const [internalValue, setInternalValue] = React.useState(defaultValue)
  const currentValue = value ?? internalValue
  
  const handleChange = (newValue: string) => {
    setInternalValue(newValue)
    onValueChange?.(newValue)
  }
  
  return (
    <TabsContext.Provider value={{ value: currentValue, onValueChange: handleChange }}>
      <div className={className}>{children}</div>
    </TabsContext.Provider>
  )
}

interface TabsListProps {
  className?: string
  style?: React.CSSProperties
  children: React.ReactNode
}

export function TabsList({ className = '', style, children }: TabsListProps) {
  const { isDiary } = useTheme()
  
  return (
    <div 
      role="tablist"
      className={`inline-flex h-10 items-center justify-center rounded-md p-1 overflow-x-auto ${
        !isDiary ? 'bg-surface-alt text-secondary' : ''
      } ${className}`}
      style={isDiary ? {
        backgroundColor: 'var(--paper-aged)',
        border: '1px solid var(--paper-margin)',
        borderRadius: '8px',
        ...style,
      } : style}
    >
      {children}
    </div>
  )
}

interface TabsTriggerProps {
  value: string
  className?: string
  style?: React.CSSProperties
  children: React.ReactNode
}

export function TabsTrigger({ value, className = '', style, children }: TabsTriggerProps) {
  const context = React.useContext(TabsContext)
  const { isDiary } = useTheme()
  if (!context) throw new Error('TabsTrigger must be used within Tabs')
  
  const isActive = context.value === value
  
  // Diary theme styles - Tab divider / bookmark aesthetic
  const getDiaryStyles = (): React.CSSProperties => {
    if (isActive) {
      return {
        backgroundColor: 'var(--paper-cream)',
        color: 'var(--ink-sepia)',
        borderBottom: '2px solid var(--bookmark-red)',
        boxShadow: '0 2px 4px var(--shadow-color, rgba(92, 64, 51, 0.1))',
        fontFamily: 'var(--font-ui)',
      }
    }
    return {
      backgroundColor: 'transparent',
      color: 'var(--ink-faded)',
      fontFamily: 'var(--font-ui)',
    }
  }
  
  return (
    <button
      type="button"
      role="tab"
      aria-selected={isActive}
      onClick={() => context.onValueChange(value)}
      className={`
        inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 
        text-sm font-medium transition-all 
        focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-surface 
        disabled:pointer-events-none disabled:opacity-50
        ${!isDiary ? (isActive 
          ? 'bg-brand text-on-brand shadow-sm' 
          : 'text-secondary hover:text-primary'
        ) : (isActive ? '' : 'hover:bg-paper-cream hover:text-[var(--ink-sepia)]')}
        ${className}
      `}
      style={isDiary ? { ...getDiaryStyles(), ...style } : style}
      data-state={isActive ? 'active' : 'inactive'}
    >
      {children}
    </button>
  )
}

interface TabsContentProps {
  value: string
  className?: string
  style?: React.CSSProperties
  children: React.ReactNode
}

export function TabsContent({ value, className = '', style, children }: TabsContentProps) {
  const context = React.useContext(TabsContext)
  const { isDiary } = useTheme()
  if (!context) throw new Error('TabsContent must be used within Tabs')
  
  if (context.value !== value) return null
  
  return (
    <div 
      role="tabpanel"
      tabIndex={0}
      className={`mt-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-surface ${className}`}
      style={isDiary ? {
        fontFamily: 'var(--font-body)',
        ...style,
      } : style}
    >
      {children}
    </div>
  )
}

